import { api } from "./api";
import { ListRequest } from "../types/common/commonTypes";
import {
  LocationCreate,
  LocationFull,
  PaginatedLocationResponse,
} from "../types/locationTypes";

/**
 * Location Service
 *
 * Manages hierarchical location structure for inventory management.
 * Supports parent-child relationships and location-based inventory tracking.
 *
 * API Endpoints:
 * - POST /api/v1/inventory/locations/ - Create location
 * - GET /api/v1/inventory/locations/ - List locations with filtering
 * - GET /api/v1/inventory/locations/{id} - Get location details
 * - PUT /api/v1/inventory/locations/{id} - Update location
 * - DELETE /api/v1/inventory/locations/{id} - Soft delete location
 * - POST /api/v1/inventory/locations/{id}/restore - Restore location
 * - GET /api/v1/inventory/locations/{id}/inventory-summary - Get location inventory summary
 */
const loc_base = "/locations";
export const locationService = {
  list: async (params: ListRequest): Promise<PaginatedLocationResponse> => {
    const response = await api.post(`${loc_base}/search`, params);
    return response.data;
  },

  /**
   * Get detailed information about a specific location
   */
  getById: async (id: string): Promise<LocationFull> => {
    const response = await api.get(`${loc_base}/${id}`);
    return response.data;
  },

  /**
   * Create a new location
   * Supports hierarchical structure with parent-child relationships
   */
  create: async (data: LocationCreate): Promise<LocationFull> => {
    const response = await api.post(`${loc_base}/`, data);
    return response.data;
  },

  /**
   * Update a location
   */
  update: async (
    id: string,
    data: Partial<LocationCreate>,
  ): Promise<LocationFull> => {
    const response = await api.put(`${loc_base}/${id}`, data);
    return response.data;
  },

  /**
   * Soft delete a location
   * Marks location as deleted but preserves audit trail
   */
  deactivate: async (id: string): Promise<void> => {
    await api.delete(`${loc_base}/${id}`);
  },

  /**
   * Restore a soft-deleted location
   */
  reactivate: async (id: string): Promise<LocationFull> => {
    const response = await api.post(`${loc_base}/${id}/restore`);
    return response.data;
  },
};
